'use client';

import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useMemo } from 'react';

// useLayoutEffect warns when run on the server; fall back to useEffect during SSR.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

type DeviceContextType = {
  deviceType: DeviceType;
  setDeviceType: React.Dispatch<React.SetStateAction<DeviceType>>;
};

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const useDevice = () => {
  const context = useContext(DeviceContext);

  if (!context) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }

  return context;
};

// Breakpoints match the previous innerWidth checks: <768 mobile, <1024 tablet.
const MOBILE_QUERY = '(max-width: 767px)';
const TABLET_QUERY = '(min-width: 768px) and (max-width: 1023px)';

const resolveDeviceType = (mobile: MediaQueryList, tablet: MediaQueryList): DeviceType =>
  mobile.matches ? 'mobile' : tablet.matches ? 'tablet' : 'desktop';

export const DeviceProvider = ({
  children,
  initialDeviceType = 'desktop',
}: {
  children: React.ReactNode;
  initialDeviceType?: DeviceContextType['deviceType'];
}) => {
  const [deviceType, setDeviceType] = useState(initialDeviceType);

  // Sync state to the real viewport before the browser paints to avoid a flash
  // when the SSR default (desktop) doesn't match the user's device. matchMedia
  // (instead of reading window.innerWidth on every resize event) avoids forced
  // synchronous layout during load and only fires when a breakpoint is crossed.
  useIsomorphicLayoutEffect(() => {
    const mobile = window.matchMedia(MOBILE_QUERY);
    const tablet = window.matchMedia(TABLET_QUERY);

    const sync = () => setDeviceType(resolveDeviceType(mobile, tablet));

    sync();
    mobile.addEventListener('change', sync);
    tablet.addEventListener('change', sync);

    return () => {
      mobile.removeEventListener('change', sync);
      tablet.removeEventListener('change', sync);
    };
  }, []);

  const value = useMemo(() => ({ deviceType, setDeviceType }), [deviceType]);

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};
